import Theme from "/components/Theme";
import FeaturedImage from "../../components/organisms/common/FeaturedImage";
import FeaturesGrid from "../../components/organisms/common/FeaturesGrid";
import BoxShadowCard from "../../components/organisms/common/BoxShadowCard";
import Hero from "../../components/organisms/common/Hero";
import CutoutFeaturedImage from "../../components/organisms/common/CutoutFeaturedImage";
import CTA from "../../components/organisms/common/CTA";
import FAQ from "../../components/organisms/common/FAQ";
import People from "../../components/organisms/common/People";
import WideCTA from "../../components/organisms/common/WideCTA";
import AppStorage from "../../components/AppStorage";
import TpWidget from "../../components/atoms/document/TpWidget";
import {
  viewport,
  colors,
  font,
  lineHeight,
} from "../../styles/cssinjs/helpers/variables.js";

export async function getServerSideProps({ req, params, locale }) {
  let allData = {};
  const countryCodeGoogle = req.headers["x-geo-country"] || "US";
  let { page } = params;
  const countrySpecificPagePath = ["us", "aus", "ca", "uk"];

  // Fetch
  const headerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/header.json`
  );

  const footerResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/common/footer.json`
  );

  // Convert
  allData.header = await headerResponse.json();
  allData.footer = await footerResponse.json();

  // Regional Redirects
  if (!countrySpecificPagePath.includes(page)) {
    let redirectDesitation;
    switch (countryCodeGoogle.toString().toLowerCase()) {
      case "us":
        redirectDesitation = "/square-payment/us";
        break;
      case "gb":
        redirectDesitation = "/square-payment/uk";
        break;
      case "ca":
        redirectDesitation = "/square-payment/ca";
        break;
      case "au":
        redirectDesitation = "/square-payment/aus";
        break;
      default:
        redirectDesitation = "/square-payment/us";
    }
    return {
      redirect: {
        destination: `/${redirectDesitation}`,
        permanent: true,
      },
    };
  }

  // Fetch
  const marketingResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/integrations-standalone/square-payment/${page}/marketing.json`
  );

  const contentResponse = await fetch(
    `${process.env.JSON_PATH}/${locale}/integrations-standalone/square-payment/${page}/content.json`
  );

  // Catch Error
  if (marketingResponse.status === 404 || contentResponse.status === 404) {
    return {
      notFound: true,
    };
  }

  // Convert
  allData.marketing = await marketingResponse.json();
  allData.content = await contentResponse.json();

  // Wrap
  const header = {
    header: allData.header,
  };
  const footer = {
    footer: allData.footer,
  };

  // Combine all data
  const data = await {
    ...header,
    ...footer,
    ...allData.marketing,
    ...allData.content,
  };

  return {
    props: {
      data,
    },
  };
}

const FeaturesGridSection = ({ data }) => {
  return (
    <section className="bg-light lg:pt-4">
      <FeaturesGrid data={data.list} colCount={data.colCount} />
    </section>
  );
};
const BoxShadowCardSection = ({ data, className }) => {
  return (
    <section className={className}>
      <article className="container md:mb-5">
        <h2
          className="mx-auto text-xl lg:text-center md:text-left md:mb-2.5 lg:maxw-2xl lg:px-10"
          dangerouslySetInnerHTML={{ __html: data.title }}
        />
        <p
          className="mx-auto lg:mt-4 lg:mb-10 lg:text-center lg:maxw-xl"
          dangerouslySetInnerHTML={{ __html: data.desc }}
        />
      </article>
      <article className="pos-rel">
        <BoxShadowCard data={data.list} hasLink={data.hasLink} />
        <style jsx>{`
          article::after {
            content: "";
            position: absolute;
            bottom: 0;
            left: 0;
            width: 100%;
            height: calc(100% - 110px);
            background-color: ${colors.primitive.black[200]};
            z-index: -1;
          }
          @media (max-width: ${viewport.tablet.sm}) {
            article::after {
              height: calc(100% - 44px);
            }
          }
        `}</style>
      </article>
    </section>
  );
};

const FeaturedImagesSection = ({ data, wrapClassNames = "" }) => {
  return (
    <section className={`${wrapClassNames}`}>
      {data.map((item, index) => {
        return (
          <FeaturedImage
            key={index}
            data={item}
            imageAlign={item.image.align}
          />
        );
      })}
      <style jsx>{`
        :global(.grid--default:last-child) {
          margin-bottom: 0px !important;
          padding-bottom: 80px;
        }
        @media (min-width: ${viewport.tablet.sm}) {
          :global(.grid--default:last-child) {
            margin-bottom: 0px !important;
            padding-bottom: 160px;
          }
        }
      `}</style>
    </section>
  );
};

export default function StandAlonePages({ data, nonce }) {
  const { header, footer, seo, content } = data;
  return (
    <Theme
      header={header}
      footer={footer}
      seo={seo}
      nonce={nonce}
      className={content.helpers.pagename}
    >
      <AppStorage pageName={`Square Payments Page`} />
      <Hero data={content.hero} />
      <CutoutFeaturedImage
        data={content.cutOutFeaturedImage}
        className="lg:pb-10 md:pb-10"
      />
      <FeaturesGridSection data={content.featuresGrid} />
      <CTA
        data={content.cta}
        className="bg-offset-top lg:pt-20 lg:pb-20 md:pt-10 md:pb-10"
      />
      <FeaturedImagesSection data={content.featuredImageSectionOne} />

      <BoxShadowCardSection
        data={content.integrationSpecificFeatures}
        className="industry-features"
      />
      <FeaturedImagesSection
        data={content.featuredImageSectionTwo}
        wrapClassNames="bg-light lg:pt-20 md:pt-10"
      />
      <FAQ data={content.faqs} className="lg:mb-15 md:mb-10 bg-offset-top" />
      <People />
      <WideCTA data={content.wideCTA} />
      <style global jsx>{`
        :global(.faq-section h2) {
          margin-bottom: 40px !important;
        }
        :global(.use-setmore-wrap img) {
          aspect-ratio: 560/ 400 !important;
        }
        @media (min-width: ${viewport.tablet.sm}) {
          :global(.hero--default) {
            padding-bottom: 0 !important;
          }
          :global(.faq-section h2) {
            margin-bottom: 80px !important;
          }
        }
        @media (min-width: ${viewport.tablet.sm}) {
          .integration-listing-page .cta-contrast.has-image h2 {
            font-size: ${font.size.lg[1]};
            line-height: ${lineHeight[10]};
          }
        }
        @media (max-width: ${viewport.tablet.sm}) {
          .bg-offset-top::before {
            content: "";
            height: 120px;
          }
          :global(.cta-contrast .btn-ternary) {
            height: initial;
          }
        }
      `}</style>
      <TpWidget />
    </Theme>
  );
}
